import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import {ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, filter, Observable, Subject, takeUntil } from 'rxjs';
import { tap } from 'rxjs';
import { RouteContextService } from '../../../context/services/route-context.service';
import { Direction } from '../../../common/enums/direction';
import { ButtonWidget } from '../../models/button-widget';
import { DeviceDetectorService } from '../../../common/device-detection/services/device-detector.service';
import { DashboardScrollService } from '../../../dashboards/services/dashboard-scroll.service';
import { OverviewService } from '../../services/overview.service';
import { RouteContextItemType } from '@context/old_index';
import { EntityKeyService } from '@feature/pma-entity/services/entity-key.service';
import { EntityKey, emptyEntityKey } from '@feature/pma-entity/pma-entity-types';
import { Actions, ofType } from '@ngrx/effects';
import * as overviewActions from "@overview/actions/overview";
import {EntityDataLayoutService} from "@layout/layouts/entity-data/entity-data-layout.service";

@Component({
  selector: 'mojo-overview',
  templateUrl: './overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewComponent
  implements OnInit, OnDestroy {

  tilesSubject$ = new BehaviorSubject<Array<ButtonWidget>>(undefined);
  tiles$: Observable<Array<ButtonWidget>> = this.tilesSubject$.asObservable();
  routeContextItemType: RouteContextItemType;
  scrollDirection$: Observable<Direction>;
  scrollDown: Direction = Direction.Down;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly contextService: RouteContextService,
    private readonly overviewService: OverviewService,
    private readonly deviceDetectorService: DeviceDetectorService,
    private readonly router: Router,
    private readonly dashboardScrollService: DashboardScrollService,
    private readonly entityKeyService: EntityKeyService,
    private readonly actions$: Actions,
    private readonly entityDataLayoutService: EntityDataLayoutService) {
  }

  ngOnInit() {

    this.entityKeyService.getEntityKey().pipe(
      takeUntil(this.destroy$),
      tap((entityKey: EntityKey) => {
        if (entityKey === emptyEntityKey) {
          this.applyEntityKey(null);
        } else {
          this.applyEntityKey(entityKey);
        }
      })).subscribe();

    const overviewLoadComplete$ = this.actions$.pipe(
      ofType<overviewActions.LoadComplete>(overviewActions.LOAD_COMPLETE)
    );
    combineLatest([overviewLoadComplete$,
      this.entityDataLayoutService.ActivatedRoute$]).pipe(
      takeUntil(this.destroy$),
      tap(([loadCompleted, activeRoute]) => {
        this.setTilesFromRouteData(activeRoute);
      })
    ).subscribe();

    this.scrollDirection$ = this.dashboardScrollService.getScrollDirection();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyEntityKey(entityKey: EntityKey) {
    this.overviewService.loadOverview(entityKey);
  }

  setTilesFromRouteData(activatedRoute: ActivatedRoute) {
    console.debug(activatedRoute);
    const routeContext = this.contextService.getRouteContextFromRouteData(activatedRoute);
    this.routeContextItemType = routeContext ? routeContext.routeContextItemType : null;
    this.overviewService.getOverviewTiles(this.router.url.split(/[?#]/)[0], this.routeContextItemType).pipe(
      filter(x => x?.length > 0)
    ).subscribe(x => this.tilesSubject$.next(x));
  }

  isMobile(): boolean {
    return this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();
  }
}

